import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
  Method,
} from 'axios';

import type { BaseApiResponse } from '@/package';

type AxiosActionHistoryLog = {
  config: Pick<AxiosRequestConfig, 'baseURL' | 'data' | 'headers' | 'params'>;
  duration: number;
  error: Nullish<{
    message: string;
    status: Nullish<number>;
  }>;
  method: Nullish<Method>;
  response: { trID?: string } & Nullish<
    Pick<AxiosResponse, 'headers' | 'status'>
  >;
  timestamp: string;
  url: string;
};

// request 시작 시간을 저장하기 위한 확장 타입
type ExtendedAxiosRequestConfig = InternalAxiosRequestConfig & {
  _startTime?: number;
};

class AxiosActionHistory {
  static MAX_HISTORY = 10;
  static instance: AxiosActionHistory;

  actionHistory = [] as AxiosActionHistoryLog[];

  constructor() {
    if (AxiosActionHistory.instance) return AxiosActionHistory.instance;

    AxiosActionHistory.instance = this;
  }

  cleanup() {
    this.actionHistory = [];

    return this;
  }

  push(log: AxiosActionHistoryLog) {
    this.actionHistory.push(log);

    this.actionHistory.length > AxiosActionHistory.MAX_HISTORY &&
      this.actionHistory.shift();
  }

  // request interceptor에서 호출
  onRequest(config: ExtendedAxiosRequestConfig) {
    config._startTime = Date.now();

    return config;
  }

  // response interceptor에서 호출 (성공)
  onResponse(response: AxiosResponse<BaseApiResponse<any>>) {
    const config = response.config as ExtendedAxiosRequestConfig;
    const startTime = config._startTime ?? Date.now();
    const duration = Date.now() - startTime;

    const log: AxiosActionHistoryLog = {
      config: {
        baseURL: config.baseURL,
        data: config.data,
        headers: config.headers,
        params: config.params,
      },
      duration,
      error: null,
      method: config.method as Method,
      response: {
        trID: response.data?.trID ?? null,
        headers: response.headers,
        status: response.status,
      },
      timestamp: new Date().toISOString(),
      url: config.url ?? '',
    };

    this.push(log);

    return response;
  }

  // response interceptor에서 호출 (에러)
  onResponseError(error: AxiosError<BaseApiResponse<any>>) {
    const config = error.config as ExtendedAxiosRequestConfig | undefined;
    const startTime = config?._startTime ?? Date.now();
    const duration = Date.now() - startTime;

    const log = {
      config: {
        baseURL: config?.baseURL,
        data: config?.data,
        headers: config?.headers,
        params: config?.params,
      },
      duration,
      error: {
        message: error.message,
        status: error.response?.status ?? null,
      },
      method: config?.method as Method,
      response: error.response
        ? {
            trID: (error.response.data as any)?.trID ?? null,
            headers: error.response.headers,
            status: error.response.status,
          }
        : null,
      timestamp: new Date().toISOString(),
      url: config?.url ?? '',
    } as AxiosActionHistoryLog;

    this.push(log);

    return Promise.reject(error);
  }
}

export const axiosActionHistory = new AxiosActionHistory();
